import { Children, isValidElement } from 'react';
import { Box, CardContent, CardMedia, Checkbox, Chip, FormLabel, Icon, IconButton, Typography, useMediaQuery } from '@mui/material';

import Skeleton from 'Lib/components/common/table/FilesCardSkeleton';
import { ListTableToolbar } from 'Lib/components/common/table/index';
import { FileActionBox, FileCard, FileCardMobile, FilesContainer, FolderImage } from 'Lib/components/common/table/StyledComponents';
import ModalTrigger from 'Lib/muiComponents/modal/ModalTrigger';
import { getFileIcon, isPath } from 'Lib/utilities/files';
import getIn from 'Lib/utilities/getIn';
import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';
import userDevice from 'Lib/utilities/userDevice';

/**
 * Renders a card view of files or folders with additional details.
 *
 * Props:
 * - `data`: Array of objects representing each file or folder to be displayed.
 * - `loading`: Boolean to indicate if the data is still being loaded.
 * - `renderDetails`: Function that returns the details to be displayed for each item.
 * - `selected`: Array of currently selected items.
 * - `handleCheckboxSelect`: Function to handle selection of an item.
 * - `onRowClick`: Function to handle click events on a row.
 * - `checkable`: Function to determine the unique identifier for each item.
 *
 */

const Detail = ({ label, content, maxWidth, chip }) => {
    const mobile = useMediaQuery(theme => theme.breakpoints.down('mobile'));

    return (
        <Box p={0} display="flex" flexDirection="column" height="min-content" width="100%" sx={{ maxWidth }}>
            <FormLabel variant="list-filter">{label}</FormLabel>

            {chip && mobile && content !== '--' ? (
                <Chip variant="selectable" label={content} sx={{ maxWidth: 'fit-content' }} />
            ) : (
                <Typography noWrap sx={{ lineHeight: '20px', maxWidth: content.length > 19 && mobile ? '45vw' : '100%' }}>
                    {content}
                </Typography>
            )}
        </Box>
    );
};

const DataCard = ({ data, renderDetails, index, selected, onCheckboxChange, onRowClick }) => {
    const { thumbnail, name, shared, date, from, download, actions } = renderDetails(data, index);

    const mobile = useMediaQuery(theme => theme.breakpoints.down('mobile'));
    const { smallMobile } = screenSize();

    const hasDateCreated = isValidElement(date) && date.props.value;

    return mobile ? (
        <FileCardMobile smallMobile={smallMobile}>
            <Box position="absolute" right={0} p={3} zIndex={1}>
                {actions(data)}
            </Box>
            <Box position="relative" minWidth={122}>
                {thumbnail ? (
                    <ModalTrigger name="File Preview" state={data.file} sx={{ height: '100%', width: '100%' }}>
                        <CardMedia sx={{ height: 'inherit', width: 'inherit' }} image={thumbnail} title={name} />
                    </ModalTrigger>
                ) : (
                    <FolderImage mobile={mobile} smallMobile={smallMobile} onClick={() => onRowClick(data)}>
                        <Icon
                            variant="filled"
                            sx={theme => ({
                                fontSize: theme.typography.pxToRem(48),
                                color: 'common.black',
                                position: 'absolute',
                                top: theme.spacing(10)
                            })}
                        >
                            {isPath(data) ? 'folder_open' : getFileIcon(data)}
                        </Icon>
                    </FolderImage>
                )}
                <FileActionBox>
                    <Checkbox color="success" checked={selected} onChange={event => onCheckboxChange(event, { ...data, index })} />
                    <IconButton variant="files-card" onClick={download} sx={theme => ({ borderRadius: theme.spacing(2) })}>
                        download
                    </IconButton>
                </FileActionBox>
            </Box>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 3, py: 3 }}>
                <Detail label="Name" content={name} />
                <Box display="flex" flexDirection={smallMobile ? 'column' : 'row'} gap={3} justifyContent="space-between">
                    {hasDateCreated && <Detail label="Date Created" content={date} maxWidth={smallMobile ? '100%' : '50%'} />}
                    <Detail chip label="From" content={from} maxWidth={smallMobile ? '100%' : '50%'} />
                </Box>
                <Box display="flex">
                    <Detail label="Shared" content={shared} />
                </Box>
            </CardContent>
        </FileCardMobile>
    ) : (
        <FileCard>
            <Box position="absolute" right={0} p={3} zIndex={1}>
                {actions(data)}
            </Box>
            {thumbnail ? (
                <ModalTrigger name="File Preview" state={data.file} sx={{ width: '100%' }}>
                    <CardMedia sx={{ height: 122, width: 'inherit' }} image={thumbnail} title={name} />
                </ModalTrigger>
            ) : (
                <FolderImage onClick={() => onRowClick(data)}>
                    <Icon variant="filled" sx={theme => ({ fontSize: theme.typography.pxToRem(48), color: 'common.black' })}>
                        {isPath(data) ? 'folder_open' : getFileIcon(data)}
                    </Icon>
                </FolderImage>
            )}
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
                <Detail label="Name" content={name} />
                <Box display="flex" justifyContent="space-between">
                    <Detail label="Shared" content={shared} maxWidth="50%" />
                    {hasDateCreated && <Detail label="Date Created" content={date} />}
                </Box>
                <Box display="flex" justifyContent="space-between">
                    <Detail label="From" content={from} />
                    <Checkbox color="success" checked={selected} onChange={event => onCheckboxChange(event, { ...data, index })} />
                </Box>
            </CardContent>
        </FileCard>
    );
};

export default function FilesCardTable({ data, loading, renderDetails, selected, ...props }) {
    const isItemSelected = item => props.checkable && !!selected.find(s => getIn(s, props.checkable(s)) === getIn(item, props.checkable(item)));
    const { mobile, smallMobile } = screenSize();
    const { mobile: mobileDevice } = userDevice();

    return (
        <FilesContainer elevation={0}>
            <Box
                display="flex"
                flexWrap="wrap"
                gap={4}
                overflow="auto"
                pb={mobile ? 0 : 4}
                pr={mobile && mobileDevice ? (smallMobile ? 0 : 8) : smallMobile ? 0 : 4}
                pl={mobile && !smallMobile ? 8 : 4}
                sx={theme => ({ height: `calc(100% - ${theme.spacing(mobile ? 0 : 14)})` })}
            >
                {loading ? (
                    <Skeleton />
                ) : (
                    data.map((datum, index) => (
                        <DataCard
                            key={index}
                            index={index}
                            data={datum}
                            selected={isItemSelected(datum)}
                            renderDetails={renderDetails}
                            onCheckboxChange={props.handleCheckboxSelect}
                            onRowClick={props.onRowClick}
                        />
                    ))
                )}
            </Box>
            <ListTableToolbar loading={loading} files count={data.length} selected={selected.length} actions={props.selectedActions} />
        </FilesContainer>
    );
}
